/***
 * Sync Media Player
 */

import {ILocator, ILocatorData, ISimpleLocatorData} from '@colibrio/colibrio-reader-framework/colibrio-core-locator';

import {
    ContainerContentBlockType,
    ContentBlockTargetFetchMode,
    IContentBlock,
    IContentLocation,
    IFetchNavigationItemReferencesResult,
    IReaderPublication,
    IReaderPublicationNavigationItemReference,
    IReaderViewAnnotationLayer,
    IReadingSystemEngine,
    ISyncMediaEngineEvent,
    ISyncMediaPlaybackRange,
    ISyncMediaPlaybackRangeOptions,
    ISyncMediaPlayer,
    ISyncMediaRangeEngineEvent,
    ISyncMediaTimeline,
    ISyncMediaTimelineRangeData, ITtsUtteranceData,
    MediaContentBlockType,
    NavigationCollectionType,
    SyncMediaRangeSeekBehavior,
    TextContentBlockType,
} from '@colibrio/colibrio-reader-framework/colibrio-readingsystem-base';
import {
    ISyncMediaTtsTimelineBuilderOptions,
    SyncMediaTimelinePosition,
} from '@colibrio/colibrio-reader-framework/colibrio-readingsystem-engine';
import {
    IEpubMediaOverlaySyncMediaTimelineOptions,
} from '@colibrio/colibrio-reader-framework/colibrio-readingsystem-formatadapter-epub';
import {ISpeechSynthesisVoiceCollection, WebSpeechUtils} from '../utils/WebSpeechUtils';

import {VanillaReaderAppUiDefaults} from '../VanillaReaderUI/VanillaReaderAppUiDefaults';
import {
    IVanillaReaderEscapableContentBlockContext,
    IVanillaReaderNavigationItem,
    IVanillaReaderSyncMediaOptions,
    IVanillaReaderSyncMediaRangePlaybackOptions,
    IVanillaReaderTtsOptions,
    IVanillaSyncMediaPlaybackStateData,
    IVanillaSyncMediaSegmentData, IVanillaTtsServiceProvider, VanillaMediaPlayerEscapabilityContextCallback,
    VanillaMediaPlayerPlaybackRangeRemovedCallback,
    VanillaMediaPlayerPlaybackStateChangedCallback,
    VanillaProgressEventCallback,
    VanillaVoidCallback,
} from './VanillaReaderModel';
import {VanillaReaderPublication} from './VanillaReaderPublication';
import {VanillaReaderUtils} from './VanillaReaderUtils';
import {VanillaReaderView} from './VanillaReaderView';
import {VanillaReaderWebSpeechTtsSynthesizer} from "./VanillaReaderWebSpeechTtsSynthesizer";
import {VanillaReaderEventBus} from "../VanillaReaderEventBus/VanillaReaderEventBus";

/*
* # VanillaReaderMediaPlayer
*
* ## PRIMARY COLIBRIO FRAMEWORK TYPES
*
* - ISyncMediaPlayer
* - ISyncMediaTimeline
* - IReaderPublication
* - IReaderViewAnnotationLayer
*
* ## RESPONSIBILITIES
*
* Wraps the Colibrio Reader Framework's `SyncMediaPlayer` and the `SyncMediaTimeline` to expose a more high level API
* for playback of TTS, Media Overlays.
*
* The `VanillaReaderMediaPlayer` API exposes all the familiar methods for media playback such as `play()`, `pause()`,
* `seekToNext()` and `seekToPrevious`, but you can also seek to a locator using `seekToLocator()`.
*
* * ## NOTES:
*
* For publications that are very long or very complex, the TTS timeline can take a while to build.
* To make it possible for you to design a good user experience around this, you can set a progress callback using the
* `timelineCreationProgressCallback()` method. This callback is called continuously with a number between 0 and 1
* indication the current state of the creation process.
*
* Audiobooks have their own media player called implementation `VanillaReaderAudiobookPlayer`
*
* */

export class VanillaReaderMediaPlayer {

    playerIsAttachedToView: boolean = false;

    private _colibrioReadingSystem: IReadingSystemEngine;
    private _colibrioReaderPublication: IReaderPublication | null = null;
    private _colibrioSyncMediaPlayer: ISyncMediaPlayer | null = null;
    private _colibrioSyncMediaTimeline: ISyncMediaTimeline | null = null;
    private _vanillaReaderPublication: VanillaReaderPublication | null = null;
    private _syncMediaTtsColibrioAnnotationLayer: IReaderViewAnnotationLayer;
    private _currentActiveNavigationItems: IVanillaReaderNavigationItem[] | undefined;
    private _ttsVoice: SpeechSynthesisVoice | undefined;
    private _ttsSynthesizer: IVanillaTtsServiceProvider | undefined;
    private _vanillaReaderRangePlaybackOptions: IVanillaReaderSyncMediaRangePlaybackOptions;
    private _escapableContentBlockTypes: (ContainerContentBlockType | MediaContentBlockType | TextContentBlockType)[] = [];
    private _escapabilityContext: IVanillaReaderEscapableContentBlockContext[] = [];

    private _mediaPlayerPlaybackStateChangedCallback: VanillaMediaPlayerPlaybackStateChangedCallback | undefined;
    private _mediaPlayerRangePlaybackEndReachedCallback: VanillaMediaPlayerPlaybackStateChangedCallback | undefined;
    private _mediaPlayerRangePlaybackRangeRemovedCallback: VanillaMediaPlayerPlaybackRangeRemovedCallback | undefined;
    private _attachedCallback: VanillaVoidCallback | undefined;
    private _detachedCallback: VanillaVoidCallback | undefined;
    private _createdCallback: VanillaVoidCallback | undefined;
    private _escapabilityContextChangedCallback: VanillaMediaPlayerEscapabilityContextCallback | undefined;

    constructor(
        private _vanillaReaderView: VanillaReaderView,
        private _vanillaReaderSyncMediaOptions?: IVanillaReaderSyncMediaOptions,
        private _timelineCreationProgressCallback?: VanillaProgressEventCallback,
    ) {
        this._colibrioReadingSystem = this._vanillaReaderView.colibrioReaderView.getReadingSystemEngine();
        this._vanillaReaderPublication = this._vanillaReaderView.getVanillaReaderPublication();
        this._ttsSynthesizer = this._vanillaReaderSyncMediaOptions?.webSpeechTtsSynthesizer;
        this._syncMediaTtsColibrioAnnotationLayer = this._vanillaReaderView.colibrioReaderView.createAnnotationLayer('syncmedia-tts-highlights');
        this._vanillaReaderRangePlaybackOptions = {
            rangeHighlightColor: this._vanillaReaderSyncMediaOptions?.rangePlaybackOptions?.rangeHighlightColor || VanillaReaderAppUiDefaults.highlightColorValues.green,
            rangeHighlightVisible: this._vanillaReaderSyncMediaOptions?.rangePlaybackOptions?.rangeHighlightVisible || true,
        }
    }

    public get escapabilityContext(): IVanillaReaderEscapableContentBlockContext[] {
        return this._escapabilityContext;
    }

    onEscapabilityContextChanged(callback: VanillaMediaPlayerEscapabilityContextCallback): void {
        this._escapabilityContextChangedCallback = callback;
    }

    onCreated(callback: VanillaVoidCallback): void {
        this._createdCallback = callback;
    }
    onPlaybackStateChanged(callback: VanillaMediaPlayerPlaybackStateChangedCallback): void {
        this._mediaPlayerPlaybackStateChangedCallback = callback;
    }

    onRangePlaybackRemoved(callback: VanillaMediaPlayerPlaybackRangeRemovedCallback): void {
        this._mediaPlayerRangePlaybackRangeRemovedCallback = callback;
    }

    onRangePlaybackEndReached(callback: VanillaMediaPlayerPlaybackStateChangedCallback): void {
        this._mediaPlayerRangePlaybackEndReachedCallback = callback;
    }

    onAttached(callback: VanillaVoidCallback): void {
        this._attachedCallback = callback;
    }

    onDetached(callback: VanillaVoidCallback): void {
        this._detachedCallback = callback;
    }

    get activePlaybackRange():  ISyncMediaPlaybackRange | null {
        return this._colibrioSyncMediaPlayer?.getPlaybackRange() || null;
    }

    attach(): void {
        if (this._colibrioSyncMediaPlayer) {

            this._vanillaReaderView.colibrioReaderView.setSyncMediaPlayer(this._colibrioSyncMediaPlayer);
            this._syncMediaTtsColibrioAnnotationLayer?.setVisible(true);
            this.playerIsAttachedToView = true;

            if (this._attachedCallback) {
                this._attachedCallback();
            }
        }
    }

    detach(): void {
        if (this._colibrioSyncMediaPlayer) {
            this._vanillaReaderView.colibrioReaderView.setSyncMediaPlayer(null);
            this._syncMediaTtsColibrioAnnotationLayer?.setVisible(false);
            this.playerIsAttachedToView = false;

            if (this._detachedCallback) {
                this._detachedCallback();
            }
        }
    }

    toggle(attach: boolean): void {
        if (attach) {
            this.attach();
        } else {
            this.detach();
        }
    }

    play(): void {
        if (this._colibrioSyncMediaPlayer && this.playerIsAttachedToView) {
            this._colibrioSyncMediaPlayer.play();
        }
    }

    pause(): void {
        if (this._colibrioSyncMediaPlayer && this.playerIsAttachedToView) {
            this._colibrioSyncMediaPlayer.pause();
        }
    };

    playTimelineRange(timelineRange: ISyncMediaTimelineRangeData, options?: ISyncMediaPlaybackRangeOptions): void {
        if (this._colibrioSyncMediaPlayer && this.playerIsAttachedToView) {
            let startPosition = new SyncMediaTimelinePosition(this._colibrioSyncMediaPlayer.getTimeline(), timelineRange.start.segmentIndex, timelineRange.start.offsetWithinSegmentMs);
            let endPosition = new SyncMediaTimelinePosition(this._colibrioSyncMediaPlayer.getTimeline(), timelineRange.end.segmentIndex, timelineRange.end.offsetWithinSegmentMs);
            options = options ? options: {
                pauseOnRangeRemoved: false,
                removeRangeOnEndReached: true,
                seekBehaviour: SyncMediaRangeSeekBehavior.REMOVE_IF_OUTSIDE_RANGE
            };
            this._colibrioSyncMediaPlayer.setPlaybackRange({start: startPosition, end: endPosition}, options);
            this.play();
        }
    }

    async playLocatorRange(locator: ISimpleLocatorData, options?: ISyncMediaPlaybackRangeOptions): Promise<void> {
        if (this._colibrioSyncMediaPlayer && this.playerIsAttachedToView) {
            let location: IContentLocation | undefined = await this._vanillaReaderPublication?.getColibrioReaderPublication().fetchContentLocation(locator);
            if(!location) {
                console.warn('VanillaReaderMediaPlayer.playLocator(), Unable to fetch a valid content location');
                return;
            }
            let timeline = this._colibrioSyncMediaPlayer.getTimeline();
            let startPosition = await timeline.fetchTimelinePosition(location.collapseToStart());
            let endPosition = await timeline.fetchTimelinePosition(location.collapseToEnd());

            this.playTimelineRange({start: startPosition.toJSON(), end: endPosition.toJSON()},options);
        }
    }

    cancelRangePlayback(): void {
        this._colibrioSyncMediaPlayer?.removePlaybackRange();
    }

    seekToNext(): void {
        if (this._colibrioSyncMediaPlayer && this.playerIsAttachedToView) {
            this._colibrioSyncMediaPlayer.seekToNextSegment(true);
        }
    };

    seekToPrevious(): void {
        if (this._colibrioSyncMediaPlayer && this.playerIsAttachedToView) {
            this._colibrioSyncMediaPlayer.seekToPreviousSegment(true);
        }
    };

    seekForward(offsetMs: number = 10000): void {
        if (this._colibrioSyncMediaPlayer && this.playerIsAttachedToView) {
            this._colibrioSyncMediaPlayer.seekToApproximateTimeMs(this._colibrioSyncMediaPlayer.getApproximateElapsedTimeMs() + offsetMs);
        }
    };

    seekBackward(offsetMs: number = 10000): void {
        if (this._colibrioSyncMediaPlayer && this.playerIsAttachedToView) {
            this._colibrioSyncMediaPlayer.seekToApproximateTimeMs(this._colibrioSyncMediaPlayer.getApproximateElapsedTimeMs() - offsetMs);
        }
    };

    seekToApproximateMs(ms: number): void {
        if (this._colibrioSyncMediaPlayer && this.playerIsAttachedToView) {
            this._colibrioSyncMediaPlayer.seekToApproximateTimeMs(ms);
        }
    };

    seekToPlaybackRangeStart(): void {
        if (this._colibrioSyncMediaPlayer && this.playerIsAttachedToView && this.activePlaybackRange) {
            this._colibrioSyncMediaPlayer.seekToTimelinePosition(this.activePlaybackRange.range.start);
        }
    };

    setVolume(volume: number): void {
        if (this._colibrioSyncMediaPlayer && this.playerIsAttachedToView) {
            this._colibrioSyncMediaPlayer.setVolume(volume);
        }
    };

    setPlaybackRate(rate: number): void {
        if (this._colibrioSyncMediaPlayer && this.playerIsAttachedToView) {
            this._colibrioSyncMediaPlayer.setPlaybackRate(rate);
        }
    }

    async setTtsVoiceByName(voiceName: string | undefined): Promise<void> {
        let voicesCollection = await this._ttsSynthesizer?.fetchVoices();

        let voices = voicesCollection?.allVoices;

        if (!voices || !voiceName) {
            this._ttsVoice = undefined;
            return;
        }

        this._ttsVoice = voices.find((voice: SpeechSynthesisVoice) => {
            return voice.name.includes(voiceName);
        });

        this._ttsSynthesizer?.setActiveVoice(this._ttsVoice);
    }

    async seekToLocatorWithinVisibleRange(locator: ILocator | ILocatorData): Promise<void> {

        const syncMediaPlayer = this._colibrioSyncMediaPlayer;
        let visibleRange = this._vanillaReaderView.getVisibleRange();
        if (syncMediaPlayer && this.playerIsAttachedToView && visibleRange) {

            let timeline = syncMediaPlayer.getTimeline();
            const visibleRangeStartTimelinePosition = await timeline.fetchTimelinePosition(visibleRange.collapseToStart());
            const pos = await timeline.fetchTimelinePosition(locator);
            const contentLocation = await timeline.fetchContentLocation(pos);

            if (visibleRange.contains(contentLocation)) {
                const posAtStartOfSegment = new SyncMediaTimelinePosition(pos.getTimeline(), pos.getSegmentIndex(), 0);

                if (posAtStartOfSegment.isBefore(visibleRangeStartTimelinePosition)) {
                    syncMediaPlayer.seekToTimelinePosition(visibleRangeStartTimelinePosition);
                } else {
                    syncMediaPlayer.seekToTimelinePosition(posAtStartOfSegment);
                }

            }
        }
    }

    async seekToLocator(locator: ILocator | ILocatorData): Promise<void> {
        if (this._colibrioSyncMediaPlayer && this.playerIsAttachedToView) {

            const mediaPlayer = this._vanillaReaderView.colibrioReaderView.getSyncMediaPlayer();
            if (mediaPlayer && locator) {
                let timeline = mediaPlayer.getTimeline();
                let pos = await timeline.fetchTimelinePosition(locator);
                // Always seek to beginning of segment by setting offsetMs to 0
                let newPosition = new SyncMediaTimelinePosition(pos.getTimeline(), pos.getSegmentIndex(), 0);
                mediaPlayer.seekToTimelinePosition(newPosition);
            }
        }
    }

    destroy(): void {
    }

    public get isPaused(): boolean {
        if (this._colibrioSyncMediaPlayer) {
            return this._colibrioSyncMediaPlayer.isPaused();
        }
        return true;
    }

    public getTtsPlaybackOptions(): IVanillaReaderTtsOptions {
        return {
            rate: this._colibrioSyncMediaPlayer?.getPlaybackRate() || 1,
            volume: this._colibrioSyncMediaPlayer?.getVolume() || 1,
            highlightColor: this._vanillaReaderPublication?.ttsOptions?.highlightColor || VanillaReaderAppUiDefaults.highlightColorValues.yellow,
            voiceName: this._ttsVoice?.name,
        };
    }

    public getPlaybackOptions(): IVanillaReaderSyncMediaOptions {
        return {
            rate: this._colibrioSyncMediaPlayer?.getPlaybackRate() || 1,
            volume: this._colibrioSyncMediaPlayer?.getVolume() || 1,
            highlightColor: this._vanillaReaderPublication?.ttsOptions?.highlightColor || VanillaReaderAppUiDefaults.highlightColorValues.yellow,
        }
    }

    public getColibrioSyncMediaPlayerInstance(): ISyncMediaPlayer | null {
        return this._colibrioSyncMediaPlayer;
    }

    public setEscapabableContentBlockTypes(blockTypes: (ContainerContentBlockType | MediaContentBlockType | TextContentBlockType)[]): void {
        this._escapableContentBlockTypes = blockTypes;
    }

    public async fetchTtsVoiceCollection(): Promise<ISpeechSynthesisVoiceCollection | undefined> {
        let voicesCollection: ISpeechSynthesisVoiceCollection | undefined;

        if(this._ttsSynthesizer) {
            voicesCollection = await this._ttsSynthesizer.fetchVoices();
        } else {
            voicesCollection =  await WebSpeechUtils.fetchTtsVoices();
        }

        return voicesCollection;
    }

    /*
    *
    * PRIVATE METHODS
    *
    * */

    private _initTtsAnnotationLayer(): void {
        // The TTS SyncMedia can use an annotation layer to highlight the current reading position. Let's set up
        // the layer and set the styling options for the layer and the highlight annotations.
        this._syncMediaTtsColibrioAnnotationLayer.setLayerOptions({
            layerStyle: {
                'mix-blend-mode': 'multiply',
                opacity: '1',
            },
        });
        this._syncMediaTtsColibrioAnnotationLayer.setDefaultAnnotationOptions({
            rangeStyle: {
                'background-color': VanillaReaderAppUiDefaults.highlightColorValues.yellow,
                'opacity': '0.4',
            },
        });
    }

    private _setUpEventHandlers(): void {
    }

    async create(): Promise<void> {
        this._setUpEventHandlers();
        this._colibrioReaderPublication = this._vanillaReaderView.colibrioReaderView.getReaderPublications()[0];

        if (this._colibrioReaderPublication && (VanillaReaderUtils.isEpubReaderPublication(this._colibrioReaderPublication) || VanillaReaderUtils.isPdfReaderPublication(this._colibrioReaderPublication))) {

            // Let's see if this is publication has Media Overlays, if so we create a `IEpubMediaOverlaySyncMediaTimeline`
            if (VanillaReaderUtils.isEpubReaderPublication(this._colibrioReaderPublication) && this._colibrioReaderPublication.isMediaOverlaySyncMediaAvailable()) {
                let moOptions: IEpubMediaOverlaySyncMediaTimelineOptions = {
                    defaultAudioRendererOptions: {
                        enableMediaStreaming: true,
                    },
                };
                this._colibrioSyncMediaTimeline = await this._colibrioReaderPublication.createMediaOverlaySyncMediaTimeline(this._vanillaReaderView.colibrioReaderView.getReaderDocuments(), moOptions);
                this._colibrioSyncMediaPlayer = this._colibrioReadingSystem.createSyncMediaPlayer(this._colibrioSyncMediaTimeline, {
                    preloadAheadMs: 30000
                });

            } else {

                // This publication has no Media Overlay so let's create a TTS timeline instead.

                let wordHighlightEnabled: boolean = true;
                let highlightFirstWordOnSegmentActive: boolean = true;
                let voices:  SpeechSynthesisVoice[];
                let voiceCollection: ISpeechSynthesisVoiceCollection | undefined;

                if(!this._ttsSynthesizer) {

                    // In order to use the correct `SpeechSynthesisVoice` instance as specified in the `VanillaReaderPublication`'s
                    // `ttsOptions.voiceName` string, we need to get all available voices.
                    voiceCollection = await WebSpeechUtils.fetchTtsVoices();
                    voices =  voiceCollection.allVoices;

                    // Now we filter out the matching `SpeechSynthesisVoice` instance.
                    this._ttsVoice = voices.find((voice: SpeechSynthesisVoice) => {
                        return this._vanillaReaderPublication?.ttsOptions?.voiceName ?
                            voice.name.includes(this._vanillaReaderPublication.ttsOptions.voiceName) :
                            false;
                    });

                    this._ttsSynthesizer = new VanillaReaderWebSpeechTtsSynthesizer((utterance: SpeechSynthesisUtterance, _utteranceAdditionalData: ITtsUtteranceData | undefined): void => {
                        if (this._ttsVoice) {
                            utterance.lang = this._ttsVoice.lang;
                            utterance.voice = this._ttsVoice;
                        }
                    });
                } else {
                    voiceCollection = await this._ttsSynthesizer.fetchVoices();
                }

                VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('MEDIAPLAYER_VOICE_LIST_CHANGED', {voiceCollection}));

                if(!this._ttsSynthesizer.supportsWordBoundaries) {
                    wordHighlightEnabled = false;
                    highlightFirstWordOnSegmentActive = false;
                }

                this._initTtsAnnotationLayer();

                let syncMediaTimelineOptions: ISyncMediaTtsTimelineBuilderOptions = {
                    defaultTtsHighlightLayer: this._syncMediaTtsColibrioAnnotationLayer,
                    defaultTtsRendererOptions: {
                        highlightFirstWordOnSegmentActive,
                        wordHighlightEnabled,
                        segmentHighlightEnabled: true,
                        wordHighlightAnnotationOptions: {
                            rangeStyle: {
                                'filter': 'brightness(1)',
                                'opacity': '1',
                            },
                        },
                    },
                    defaultTtsSynthesizer: this._ttsSynthesizer,
                };

                // Now that we have the highlight layer set up we can create the SyncMedia Timeline.
                this._colibrioSyncMediaTimeline = await this._colibrioReaderPublication.createTtsSyncMediaTimeline(this._vanillaReaderView.colibrioReaderView.getReaderDocuments(), syncMediaTimelineOptions, this._timelineCreationProgressCallback);
                this._colibrioSyncMediaPlayer = this._colibrioReadingSystem.createSyncMediaPlayer(this._colibrioSyncMediaTimeline);

            }

            this.attach();
            this.playerIsAttachedToView = true;

            this._colibrioSyncMediaPlayer.addEngineEventListener('syncMediaRangeEndReached', this._onRangePlaybackEndReached);
            this._colibrioSyncMediaPlayer.addEngineEventListener('syncMediaRangeRemoved', this._onRangePlaybackRemoved);
            this._colibrioSyncMediaPlayer.addEngineEventListener('syncMediaPaused', this._onSyncMediaPlayerPaused);
            this._colibrioSyncMediaPlayer.addEngineEventListener('syncMediaPlay', this._onSyncMediaPlayerPlay);
            this._colibrioSyncMediaPlayer.addEngineEventListener('syncMediaSegmentActive', this._onSyncMediaSegmentActive);
            this._colibrioSyncMediaPlayer.addEngineEventListener('syncMediaSegmentFinished', this._onSyncMediaSegmentFinished);

            if (this._vanillaReaderSyncMediaOptions?.rate) {
                this.setPlaybackRate(this._vanillaReaderSyncMediaOptions.rate);
            }

            if (this._vanillaReaderSyncMediaOptions?.volume) {
                this.setPlaybackRate(this._vanillaReaderSyncMediaOptions.volume);
            }

            if (this._createdCallback) {
                this._createdCallback();
            }

        } else {
            console.warn('VanillaReader.syncMediaPlayer_create()', 'Cannot create SyncMediaPlayer. No Colibrio ReaderPublication loaded.');
        }
    };

    private async _fetchPlaybackStateData(ev: ISyncMediaEngineEvent): Promise<IVanillaSyncMediaPlaybackStateData> {

        if (!this._colibrioSyncMediaPlayer) {
            throw new DOMException('VanillaReaderMediaPlayer: ColibrioSyncMediaPlayer instance does not exist');
        }

        let positionMs = this._colibrioSyncMediaPlayer.getApproximateElapsedTimeMs() || 0;
        let position = this._colibrioSyncMediaPlayer.getTimelinePosition();
        let location: IContentLocation | undefined;
        if (position) {
            location = await this._colibrioSyncMediaPlayer.getTimeline()?.fetchContentLocation(position);
        }
        let rate = this._colibrioSyncMediaPlayer.getPlaybackRate();
        let volume = this._colibrioSyncMediaPlayer.getVolume();
        let isSeeking = this._colibrioSyncMediaPlayer.isSeeking();
        let isMuted = this._colibrioSyncMediaPlayer.isMuted();
        let isPaused = this._colibrioSyncMediaPlayer.isPaused();
        let locator: string | undefined;
        let navigationItems: IVanillaReaderNavigationItem[] = [];
        let navigationItemRefs: IFetchNavigationItemReferencesResult | undefined;
        let segmentData: IVanillaSyncMediaSegmentData | undefined = this._colibrioSyncMediaPlayer.getTimelinePosition().getSegment()?.toJSON();
        let segmentIndex: number | undefined = this._colibrioSyncMediaPlayer.getTimelinePosition().getSegmentIndex();
        let publicationDurationMs = this._colibrioSyncMediaPlayer.getTimeline().getApproximateDurationMs();
        let positionWithinSegment: number | undefined = this._colibrioSyncMediaPlayer.getTimelinePosition().getOffsetWithinSegmentMs();
        let playbackRange: ISyncMediaPlaybackRange | undefined = this._colibrioSyncMediaPlayer.getPlaybackRange() || undefined;

        let segmentTimelineStartPosition: SyncMediaTimelinePosition | undefined;
        segmentTimelineStartPosition = this._colibrioSyncMediaTimeline ?
            new SyncMediaTimelinePosition(this._colibrioSyncMediaTimeline, segmentIndex, 0) :
            undefined;

        if(segmentData && segmentTimelineStartPosition) {
            segmentData.segmentTimelineStartOffsetMs = segmentTimelineStartPosition.getTimeline().getApproximateOffsetMs(segmentTimelineStartPosition);
        }


        if (!this._currentActiveNavigationItems) {
            navigationItemRefs = await location?.fetchNavigationItemReferences({
                collectionTypes: [NavigationCollectionType.TOC],
                greedy: false,
            });

            navigationItemRefs?.getItemsInRange()?.forEach((ref: IReaderPublicationNavigationItemReference) => {
                navigationItems.push({
                    locator: ref.getNavigationItem()?.getLocator()?.toString(),
                    children: [],
                    collectionType: ref.getNavigationCollection().getType(),
                    title: ref.getNavigationItem().getTextContent(),
                });

            });

            // Store this value for upcoming calls. The `this._currentActiveNavigationItems` field is "reset" in the
            // `_event_segmentActive` event handler, when we have a new segment.
            this._currentActiveNavigationItems = navigationItems;
        } else {
            navigationItems = this._currentActiveNavigationItems;
        }

        if (location) {
            locator = location?.getLocator().toString();
        }

        const stateData: IVanillaSyncMediaPlaybackStateData = {
            type: ev.type,
            locator: locator,
            position: positionMs,
            positionWithinSegment,
            navigationItems: navigationItems,
            rate: rate,
            volume: volume,
            isSeeking: isSeeking,
            isMuted: isMuted,
            isPaused: isPaused,
            segmentIndex,
            segmentData: segmentData,
            publicationDurationMs: publicationDurationMs,
            playbackRangeData: (playbackRange?.range.start && playbackRange.range.end) ? {
                id: playbackRange.id,
                range: {
                    start: playbackRange.range.start.toJSON(),
                    end: playbackRange.range.end.toJSON(),
                },
                options: {
                    pauseOnRangeCanceled: playbackRange.options.pauseOnRangeRemoved,
                    removeRangeOnEndReached: playbackRange.options.removeRangeOnEndReached,
                    seekBehaviour: playbackRange.options.seekBehaviour,
                    rangeHighlightVisible: this._vanillaReaderRangePlaybackOptions.rangeHighlightVisible,
                    rangeHighlightColor: this._vanillaReaderRangePlaybackOptions.rangeHighlightColor,

                },
            } : undefined,
        };

        return Promise.resolve(stateData);
    }

    _maybeDispatchPlaybackStateChangedEvent(_ev: ISyncMediaEngineEvent): void {
        if (this._mediaPlayerPlaybackStateChangedCallback) {
            this._fetchPlaybackStateData(_ev).then((state: IVanillaSyncMediaPlaybackStateData) => {
                if (state && this._mediaPlayerPlaybackStateChangedCallback) {
                    this._mediaPlayerPlaybackStateChangedCallback(state);
                }
            }).catch(console.warn);
        }
    }

    /*
    *
    * EVENT HANDLERS
    *
    * */

    _onSyncMediaPlayerPaused = (_ev: ISyncMediaEngineEvent):void => {
        this._maybeDispatchPlaybackStateChangedEvent(_ev);
    }

    _onSyncMediaPlayerPlay = (_ev: ISyncMediaEngineEvent):void => {
        this._maybeDispatchPlaybackStateChangedEvent(_ev);
    }

    _onSyncMediaSegmentActive = async (ev: ISyncMediaEngineEvent):Promise<void> => {
        let timelinePosition = ev.timelinePosition;
        let contentLocation = await ev.syncMediaPlayer.getTimeline().fetchContentLocation(timelinePosition);

        this._escapabilityContext = await this._getEscapabilityContextFromContentLocation(contentLocation);

        if(this._escapabilityContextChangedCallback) {
            this._escapabilityContextChangedCallback(this._escapabilityContext);
        }

        this._maybeDispatchPlaybackStateChangedEvent(ev);
    }

    _onSyncMediaSegmentFinished = (ev: ISyncMediaEngineEvent):void => {

        this._maybeDispatchPlaybackStateChangedEvent(ev);
    }

    async _getEscapabilityContextFromContentLocation(contentLocation: IContentLocation): Promise<IVanillaReaderEscapableContentBlockContext[]> {

        let targets =  await contentLocation.fetchContentBlockTargets(ContentBlockTargetFetchMode.INTERSECTING);

        if(!targets || targets.length < 1) {
            return [];
        }

        let contentBlock =  targets[0].getContentBlock();

        const __getNearestEscapableContentBlock = async (contentBlock: IContentBlock, context: IContentBlock []): Promise<IContentBlock[]> => {

            if(this._escapableContentBlockTypes.includes(contentBlock.getBlockType())) {
                context.push(contentBlock)
            }

            let parentContentBlock =  contentBlock.getParent();

            if(!parentContentBlock) {
                return context;
            }

            return __getNearestEscapableContentBlock(parentContentBlock, context);
        }

        let context = await __getNearestEscapableContentBlock(contentBlock, []);

        return context.map((block)=>{
            let blockLocation = block.getContentLocation();
            return {
                blockId: block.getId(),
                block,
                blockRangeLocation: blockLocation.createRangeTo(blockLocation.collapseToEnd())
            }
        }).reverse();

    }

    _onRangePlaybackRemoved = (ev: ISyncMediaRangeEngineEvent): void => {
        if(this._mediaPlayerRangePlaybackRangeRemovedCallback) {
            this._fetchPlaybackStateData(ev).then((playbackState: IVanillaSyncMediaPlaybackStateData) => {
                if (ev.playbackRange && this._mediaPlayerRangePlaybackRangeRemovedCallback) {

                    // The just canceled range was not available to the async `_fetchPlaybackStateData` so we need
                    // to set it here where we still have the event data.
                    playbackState.playbackRangeData = {
                        id: ev.playbackRange.id,
                        range: {
                            start: ev.playbackRange.range.start.toJSON(),
                            end: ev.playbackRange.range.end.toJSON(),
                        }

                    }
                    this._mediaPlayerRangePlaybackRangeRemovedCallback(playbackState, ev.reason);
                }
            }).catch(console.warn);
        }
    }

    _onRangePlaybackEndReached = async (ev: ISyncMediaRangeEngineEvent): Promise<void> => {
        let playbackState = await this._fetchPlaybackStateData(ev);
        if(ev.playbackRange.options.removeRangeOnEndReached) {
            if(this._mediaPlayerRangePlaybackRangeRemovedCallback) {

                // The just canceled range was not available to the async `_fetchPlaybackStateData` so we need
                // to set it here where we still have the event data.
                playbackState.playbackRangeData = {
                    id: ev.playbackRange.id,
                    range: {
                        start: ev.playbackRange.range.start.toJSON(),
                        end: ev.playbackRange.range.end.toJSON(),
                    }
                }
                this._mediaPlayerRangePlaybackRangeRemovedCallback(playbackState);
            }
        }

        if (playbackState && this._mediaPlayerRangePlaybackEndReachedCallback) {
            this._mediaPlayerRangePlaybackEndReachedCallback(playbackState);
        }
    }

}
